import clsx from 'clsx';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { weekStart, type LoadLevel } from '@pz/core';
import { Avatar } from '../components/avatar';
import { LEVEL_BG, RangeNav, useWeekRange } from '../components/planning-common';
import { Checkbox, EmptyState, ErrorBox, Loading, Notice, PageHeader, Select, td, th, useTip } from '../components/ui';
import { d, h, kw } from '../lib/format';
import { useQ } from '../lib/hooks';
import { ScrollTable } from '../components/virtual';
import { useMe } from '../lib/me';

/**
 * Auslastung: who has capacity in the coming weeks (planned or booked hours against capacity).
 * A planning tool, not an assessment: personal figures are under "Mitarbeiterauswertung".
 * HR and management see hours; project leads see load levels for the people of their projects.
 */
export function WorkloadPage() {
  const { t } = useTranslation();
  const { can, isLead } = useMe();
  if (!isLead() && !can('workload.manage', 'kpi.team.view', 'hr.manage')) return <EmptyState>{t('workload.noAccess')}</EmptyState>;
  return (
    <div>
      <PageHeader title={t('workload.title')} subtitle={t('workload.subtitle')} />
      <TeamTab />
    </div>
  );
}

function TeamTab() {
  const { t } = useTranslation();
  const { me } = useMe();
  const range = useWeekRange(10);
  const [location, setLocation] = useState('');
  const [metric, setMetric] = useState<'planned' | 'booked'>('planned');
  const [flagged, setFlagged] = useState(false);
  const q = useQ('workload.team', { from: range.from, to: range.to });
  const people = useQ('people.list', {});
  const avatars = new Map((people.data?.people ?? []).map((p) => [p.id, p.avatarAt]));
  const persons = (q.data?.persons ?? []).filter((p) => (!location || p.locationCountry === location) && (!flagged || p.totals.underWeeks > 0 || p.totals.overWeeks > 0));
  // hours of capacity only for Personal (HR) and management; planners and project leads see load levels
  const full = !!q.data?.showCapacity;
  const currentWeek = weekStart(me.today);
  const tip = useTip();
  return (
    <div className="flex flex-col gap-3">
      {tip.node}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-sheet px-3 py-2">
        <RangeNav range={range} />
        <Select className="w-40" value={location} onChange={(e) => setLocation(e.target.value)} aria-label={t('workload.location')}>
          <option value="">{t('common.all')}</option>
          <option value="DE">{t('people.countries.DE')}</option>
          <option value="TR">{t('people.countries.TR')}</option>
        </Select>
        <Select className="w-40" value={metric} onChange={(e) => setMetric(e.target.value as 'planned')} aria-label={t('common.type')}>
          <option value="planned">{t('common.planned')}</option>
          <option value="booked">{t('common.booked')}</option>
        </Select>
        {q.data?.bookedState && <Checkbox label={t('workload.onlyFlagged')} checked={flagged} onChange={(e) => setFlagged(e.target.checked)} />}
        <div className="ml-auto flex flex-wrap items-center gap-2 text-xs text-graphite">
          <span>{t('workload.legend')}:</span>
          {(['low', 'ok', 'high', 'over', 'none'] as const).map((l) => (
            <span key={l} className="flex items-center gap-1.5">
              <span className={clsx('inline-block size-3 rounded-[3px] border border-line', LEVEL_BG[l])} />
              {t(`workload.level.${l}`)}
              {l === 'over' && <AlertTriangle className="size-3 text-bad" aria-hidden />}
            </span>
          ))}
          {q.data?.bookedState && (
            <>
              <span className="flex items-center gap-1">
                <ChevronUp className="size-3.5 text-warn" aria-hidden />
                {t('kpi.overLegend')}
              </span>
              <span className="flex items-center gap-1">
                <ChevronDown className="size-3.5 text-bad" aria-hidden />
                {t('kpi.underLegend')}
              </span>
            </>
          )}
        </div>
      </div>
      {q.data?.bookedState && <p className="text-xs text-graphite">{t('workload.stateHint', { hours: h(q.data.toleranceMinutes, 0) })}</p>}
      {q.data && !full && <Notice tone="neutral">{t('workload.leadPrivacy')}</Notice>}
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <ScrollTable label={t('workload.title')}>
          <table className="w-full min-w-[900px] text-[13px]">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={clsx(th, 'sticky left-0 z-10 bg-sheet-2')}>{t('common.person')}</th>
                {q.data.weeks.map((w) => (
                  <th key={w} className={clsx(th, 'text-center', w === currentWeek && 'text-accent')}>
                    {t('common.cw')} {kw(w)}
                    <span className="block font-normal">{d(w).slice(0, 6)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {persons.map((p) => (
                <tr key={p.id} className="border-b border-line/70">
                  <td className={clsx(td, 'sticky left-0 z-10 bg-sheet whitespace-nowrap')}>
                    <div className="flex items-center gap-2">
                      <Avatar personId={p.id} name={p.name} avatarAt={avatars.get(p.id) ?? null} />
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="flex items-center gap-1.5 text-xs text-graphite">
                          {[p.discipline, p.locationCountry].filter(Boolean).join(' · ')}
                          {p.totals.underWeeks > 0 && (
                            <span className="inline-flex items-center text-bad" title={t('kpi.underLegend')}>
                              <ChevronDown className="size-3" />
                              {p.totals.underWeeks}
                            </span>
                          )}
                          {p.totals.overWeeks > 0 && (
                            <span className="inline-flex items-center text-warn" title={t('kpi.overLegend')}>
                              <ChevronUp className="size-3" />
                              {p.totals.overWeeks}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  {p.weeks.map((w) => {
                    const value = metric === 'planned' ? w.plannedMinutes : w.bookedMinutes;
                    // externals have no contract capacity: hours are shown without a load colour;
                    // for project leads the booked view has no colour either (only their projects are counted)
                    const level: LoadLevel | null =
                      p.kind === 'external' ? 'none' : metric === 'planned' ? w.level : full ? w.bookedLevel : null;
                    const cap = w.capacityMinutes ?? 0;
                    const lines = [
                      `${p.name} · ${t('common.cw')} ${kw(w.weekStart)}${level ? ` · ${t(`workload.level.${level}`)}` : ''}`,
                      full
                        ? t('workload.cellTitle', { cap: h(cap), plan: h(w.plannedMinutes), booked: h(w.bookedMinutes) })
                        : t('workload.cellTitleLead', { plan: h(w.plannedMinutes), booked: h(w.bookedMinutes) }),
                      ...(w.internalMinutes > 0 ? [t('workload.internalPart', { hours: h(w.internalMinutes) })] : []),
                      ...w.holidays.map((x) => `${d(x.date)} ${x.name}`),
                      ...w.absenceKinds.map((k) => t(`week.absence.${k as 'vacation'}`)),
                      ...(w.bookedState === 'under' ? [t('kpi.under', { hours: h(cap - w.bookedMinutes) })] : []),
                      ...(w.bookedState === 'over' ? [t('kpi.over', { hours: h(w.bookedMinutes - cap) })] : []),
                    ];
                    return (
                      <td key={w.weekStart} className="p-1 text-center">
                        <div
                          tabIndex={0}
                          aria-label={lines.join('. ')}
                          {...tip.bind(
                            <div className="flex flex-col gap-0.5">
                              <span className="font-medium">{lines[0]}</span>
                              {lines.slice(1).map((l, i) => (
                                <span key={i} className="text-graphite">
                                  {l}
                                </span>
                              ))}
                            </div>,
                          )}
                          className={clsx('num relative flex items-center justify-center gap-1 rounded px-1 py-1.5 outline-none focus-visible:ring-2 focus-visible:ring-accent', level ? LEVEL_BG[level] : 'bg-sheet-2')}
                        >
                          {level === 'over' && <AlertTriangle className="size-3 shrink-0 text-bad" aria-hidden />}
                          <span>
                            <span className="font-semibold text-ink">{h(value, 0)}</span>
                            {full && <span className="text-graphite">/{h(cap, 0)}</span>}
                          </span>
                          {w.bookedState === 'under' && <ChevronDown className="size-3.5 shrink-0 text-bad" aria-hidden />}
                          {w.bookedState === 'over' && <ChevronUp className="size-3.5 shrink-0 text-warn" aria-hidden />}
                          {(w.absent || w.holidays.length > 0) && <span className="hatch absolute inset-x-0 bottom-0 h-1 rounded-b" />}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollTable>
      )}
    </div>
  );
}

